import { useState } from 'react';

import TABLE_CONSTANTS from '@constants/admin/tables';
import { Row } from 'antd';
import { SorterResult } from 'antd/es/table/interface';
import { useTranslation } from 'react-i18next';

import ResizableTable from '@/components/admin/ResizableTable';
import SectionHeading from '@/components/admin/SectionHeading';
import TextInput from '@/components/admin/TextInput';
import { ErrorTypeEnum } from '@/enums/error-type.enum';
import { SortEnumType, ThirdParty, useGetThirdPartiesQuery } from '@/graphql/main';
import usePaginate from '@/hooks/admin/usePaginate';
import useError from '@/hooks/error/useError';
import useColDefs from '@/pages/admin/CommercialAgreements/ThirdParties/hooks/useColDefs';
import CreateThirdPartiesModal from '@/pages/admin/CommercialAgreements/ThirdParties/partials/CreateThirdPartiesModal';

const ThirdPartiesTable = () => {
    const {
        handlePageChange,
        pageSize,
        itemsToSkip,
        setTotalItems,
        totalItems,
        setSortingOrder,
        sortingOrder
    } = usePaginate();
    const { setError, clearError } = useError();
    const { t } = useTranslation();
    const [searchText, setSearchText] = useState('');
    const { TableSize } = TABLE_CONSTANTS;
    const { loading, refetch, data } = useGetThirdPartiesQuery({
        variables: {
            searchText,
            take: pageSize,
            skip: itemsToSkip,
            order: sortingOrder
        },
        onCompleted: (data) => {
            if (data?.thirdParties?.items) {
                setTotalItems(data.thirdParties.totalCount);
            }
            clearError();
        },
        onError: (error) => {
            setError(error, ErrorTypeEnum.ApolloError);
        }
    });

    const { colDef } = useColDefs({
        onChangeSuccess: async () => await refetch()
    });

    const onSortChange = (sorterResult: SorterResult<ThirdParty>) =>
        setSortingOrder(
            sorterResult.order
                ? {
                      [sorterResult.columnKey as string]:
                          sorterResult.order === 'ascend' ? SortEnumType.Asc : SortEnumType.Desc
                  }
                : undefined
        );

    return (
        <>
            <SectionHeading heading={t('THIRD_PARTIES')} />
            <Row justify={'space-between'} align={'bottom'} style={{ marginBottom: 18 }}>
                <TextInput inputValue={searchText} setInputValue={setSearchText} />
                <CreateThirdPartiesModal successCallback={refetch} />
            </Row>
            <ResizableTable
                colDefs={colDef}
                tableData={(data?.thirdParties?.items as ThirdParty[]) || []}
                emptyText={t('NO_RESULTS')}
                loading={loading}
                onPageChange={handlePageChange}
                totalPaginationItems={totalItems}
                size={TableSize}
                onChange={(_, __, sorterResult) =>
                    onSortChange(sorterResult as SorterResult<ThirdParty>)
                }
            />
        </>
    );
};

export default ThirdPartiesTable;
